import { AnimatePresence, motion } from 'framer-motion';
import { Key, Mail, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export interface SignupWallModalProps {
  isOpen: boolean;
  onClose: () => void;
  trigger?: 'timed' | 'click_blurred' | 'click_disabled' | 'navigation';
  context?: 'like' | 'save' | 'dismiss' | 'navigation';
}

const COOLDOWN_KEY = 'banquinha_signup_wall_cooldown';
const COOLDOWN_MS = 10000; // 10 seconds cooldown
export const FROM_PUBLIC_FEED_KEY = 'banquinha_from_public_feed';

// Contextual messages based on user action
const MODAL_MESSAGES: Record<
  string,
  {
    headline: string;
    subheadline: string;
  }
> = {
  default: {
    headline: 'Seu feed personalizado está esperando',
    subheadline:
      'Conteúdo curado em português, sem algoritmos viciantes. Só o que importa para você.',
  },
  blurred: {
    headline: 'Esse conteúdo está esperando por você',
    subheadline: 'Crie sua conta para acessar todo o feed curado para seus interesses.',
  },
  like: {
    headline: 'Ajude a melhorar suas recomendações',
    subheadline: 'Marque conteúdo como útil para recebermos cada vez mais o que você gosta.',
  },
  save: {
    headline: 'Salve para ler depois',
    subheadline: 'Com sua conta, você pode salvar conteúdos e acessar sua biblioteca pessoal.',
  },
  dismiss: {
    headline: 'Personalize seu feed',
    subheadline: 'Crie sua conta para indicar o que não quer ver e refinar suas recomendações.',
  },
  navigation: {
    headline: 'Explore mais conteúdo',
    subheadline: 'Navegue entre os artigos e descubra mais com sua conta gratuita.',
  },
};

// Set flag that user came from public feed
function setFromPublicFeedFlag() {
  sessionStorage.setItem(FROM_PUBLIC_FEED_KEY, 'true');
}

export function SignupWallModal({
  isOpen,
  onClose,
  trigger = 'timed',
  context,
}: SignupWallModalProps) {
  const { t } = useTranslation();

  // Get contextual message
  const messageKey = context || (trigger === 'click_blurred' ? 'blurred' : 'default');
  const message = MODAL_MESSAGES[messageKey] || MODAL_MESSAGES.default;

  const handleClose = useCallback(() => {
    // Set cooldown timestamp
    localStorage.setItem(COOLDOWN_KEY, Date.now().toString());
    onClose();
  }, [onClose]);

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 bg-[rgba(46,42,35,0.5)] backdrop-blur-sm z-50"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 10 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="signup-wall-title"
              aria-describedby="signup-wall-description"
              className="relative w-full max-w-[420px] bg-[#F4F2F2] rounded-2xl shadow-[0_8px_32px_rgba(46,42,35,0.15)] pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                type="button"
                onClick={handleClose}
                className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center text-[#888185] hover:text-[#2E2A23] transition-colors rounded-lg hover:bg-[#F4F2F2] cursor-pointer"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="p-10">
                {/* Illustration */}
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#E94E1B] to-[#FDEAE3] rounded-2xl flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-[#F4F2F2]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>

                {/* Headline */}
                <h2
                  id="signup-wall-title"
                  className="font-serif text-2xl font-semibold text-[#2E2A23] text-center mb-3"
                >
                  {t(`publicFeed.signupWall.${messageKey}.headline`, message.headline)}
                </h2>

                {/* Subheadline */}
                <p
                  id="signup-wall-description"
                  className="text-[15px] leading-[1.5] text-[#888185] text-center mb-8"
                >
                  {t(`publicFeed.signupWall.${messageKey}.subheadline`, message.subheadline)}
                </p>

                {/* Social Login Buttons - Grid 2x2 */}
                <div className="grid grid-cols-2 gap-2.5 mb-6">
                  <SocialIconButton provider="google" />
                  <SocialIconButton provider="apple" />
                  <SocialIconButton provider="microsoft" />
                  <SocialIconButton provider="linkedin" />
                </div>

                {/* Divider */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-1 h-px bg-[#CAC7C7]" />
                  <span className="text-[13px] text-[#888185]">
                    {t('publicFeed.signupWall.or', 'ou')}
                  </span>
                  <div className="flex-1 h-px bg-[#CAC7C7]" />
                </div>

                {/* Magic Link Button */}
                <Link
                  to="/login"
                  onClick={setFromPublicFeedFlag}
                  className="w-full h-12 bg-[#2E2A23] text-[#F4F2F2] rounded-lg text-[15px] font-medium hover:bg-[#3D3833] transition-all hover:-translate-y-px flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Mail className="w-5 h-5" />
                  {t('publicFeed.signupWall.magicLink', 'Entrar com magic link')}
                </Link>

                {/* Password Login Button */}
                <Link
                  to="/login"
                  onClick={setFromPublicFeedFlag}
                  className="w-full h-12 mt-3 bg-white border border-[#CAC7C7] text-[#2E2A23] rounded-lg text-[15px] font-medium hover:bg-[#F4F2F2] hover:border-[#888185] transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Key className="w-5 h-5" />
                  {t('publicFeed.signupWall.password', 'Entrar com senha')}
                </Link>

                {/* Login Link */}
                <p className="text-center text-[14px] text-[#888185] mt-5">
                  {t('publicFeed.signupWall.hasAccount', 'Já tem conta?')}{' '}
                  <Link
                    to="/login"
                    onClick={setFromPublicFeedFlag}
                    className="text-[#E94E1B] hover:underline font-medium"
                  >
                    {t('publicFeed.signupWall.login', 'Entrar')}
                  </Link>
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

// Social Icon Button Component
interface SocialIconButtonProps {
  provider: 'google' | 'apple' | 'microsoft' | 'linkedin';
}

function SocialIconButton({ provider }: SocialIconButtonProps) {
  const config = {
    google: {
      label: 'Google',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      ),
    },
    apple: {
      label: 'Apple',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#000000" aria-hidden="true">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      ),
    },
    microsoft: {
      label: 'Microsoft',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#F25022" d="M1 1h10v10H1z" />
          <path fill="#00A4EF" d="M1 13h10v10H1z" />
          <path fill="#7FBA00" d="M13 1h10v10H13z" />
          <path fill="#FFB900" d="M13 13h10v10H13z" />
        </svg>
      ),
    },
    linkedin: {
      label: 'LinkedIn',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
    },
  };

  const { label, icon } = config[provider];

  if (provider === 'linkedin') {
    return (
      <button
        type="button"
        disabled
        className="h-11 bg-white border border-[#CAC7C7] rounded-lg flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
        aria-label={`Continuar com ${label}`}
      >
        {icon}
        <span className="text-[13px] font-medium text-[#2E2A23]">{label}</span>
      </button>
    );
  }

  return (
    <Link
      to="/login"
      onClick={setFromPublicFeedFlag}
      className="h-11 bg-white border border-[#CAC7C7] rounded-lg hover:bg-[#F4F2F2] hover:border-[#888185] transition-all flex items-center justify-center gap-2 cursor-pointer"
      aria-label={`Continuar com ${label}`}
    >
      {icon}
      <span className="text-[13px] font-medium text-[#2E2A23]">{label}</span>
    </Link>
  );
}

// Hook to manage signup wall visibility with cooldown
export function useSignupWall(delay = 2000) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check cooldown
    const cooldownTimestamp = localStorage.getItem(COOLDOWN_KEY);
    if (cooldownTimestamp) {
      const elapsed = Date.now() - parseInt(cooldownTimestamp, 10);
      if (elapsed < COOLDOWN_MS) {
        return;
      }
    }

    // Show modal after delay
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return { isOpen, close };
}
